import { forwardRef, useEffect, useRef, useState } from 'react';
import { Dropdown, OverlayTrigger, Tooltip } from 'react-bootstrap';
import { Link, useSearchParams } from 'react-router';
import { orderBy } from 'lodash';
import { ReactComponent as ChatIcon } from '../../assets/icons/chat_icon_header.svg';
import { EncounterIcon } from '../EncounterIcon/EncounterIcon';
import { useChat } from '../../redux/ducks/chat';
import { useOrchestrator } from '../../redux/ducks/orchestrator';
import { toName } from '../../utils/stringFormatter';
import { getProgramNameById } from '../../utils/entityUtils';
import { isBooleanTrue, isListNotEmpty, isStringNotEmpty, isStringEmpty, isListEmpty } from '../../utils/objectUtils';
import ROUTES from '../../constants/routes';
import styles from './DashboardPageHeader.module.css';

const MESSAGES_ROUTE = ROUTES.MY_MESSAGES;

/**
 * Dropdown toggle button for the V2 messages menu.
 *
 * @param {object} props
 * @param {(event: React.MouseEvent) => void} props.onClick - Dropdown toggle handler.
 * @param {boolean} props.hasUnread - Whether to show the unread indicator.
 * @param {React.Ref<HTMLButtonElement>} ref - Forwarded dropdown ref.
 * @returns {JSX.Element} Messages toggle button.
 */
const ChatToggle = forwardRef(({ onClick, hasUnread }, ref) => (
	<button
		ref={ref}
		type="button"
		onClick={(e) => { e.preventDefault(); onClick(e); }}
		className={`${styles.dashboardPageHeaderIconButton} ${styles.dashboardPageHeaderIconButtonOrange}`}
		aria-label="Messages"
	>
		<ChatIcon aria-hidden="true" />
		{isBooleanTrue(hasUnread) && <span className={styles.dashboardPageHeaderNotificationDot} />}
	</button>
));
ChatToggle.displayName = 'ChatToggle';

/**
 * Renders the dashboard header messages menu with recent encounter threads and
 * unread indicators.
 *
 * @returns {JSX.Element} Messages dropdown or empty tooltip button.
 */
export const MessagesMenu = () => {
	const [show, setShow] = useState(false);
	const closeTimeoutRef = useRef(null);
	const [searchParams] = useSearchParams();
	const { encounters, getProviderDetails, getLastMessage, getLastMessageDateTime } = useChat();
	const { programs } = useOrchestrator();

	const messageEncounters = orderBy(
		(encounters ?? []).filter((e) => isListNotEmpty(e?.messages)),
		['lastMessageAt'],
		['desc']
	);

	const hasUnread = (encounters ?? []).some((e) => (e?.unreadCount ?? 0) > 0);

	const getDisplayName = (encounter) => {
		const otherUser = getProviderDetails(encounter?.providerId ?? null);
		return encounter?.messageName
			?? toName(otherUser)
			?? (isStringNotEmpty(encounter?.programId) ? getProgramNameById(encounter?.programId, programs) : null)
			?? 'Chat Thread';
	};

	const getPreview = (encounter) => {
		const raw = getLastMessage(encounter);
		if (isStringEmpty(raw)) return 'No messages yet';
		const text = raw.replace(/<[^>]*>/g, ' ').replace(/\s+/g, ' ').trim();
		return text.length > 60 ? `${text.substring(0, 60)}...` : text;
	};

	const buildLink = (encounter) => {
		const params = new URLSearchParams(searchParams);
		params.set('encounterId', encounter.id);
		return { pathname: MESSAGES_ROUTE, search: params.toString() };
	};

	const clearCloseTimeout = () => {
		if (closeTimeoutRef.current) {
			clearTimeout(closeTimeoutRef.current);
			closeTimeoutRef.current = null;
		}
	};

	const openMenu = () => {
		clearCloseTimeout();
		setShow(true);
	};

	const closeMenuWithDelay = () => {
		clearCloseTimeout();
		closeTimeoutRef.current = setTimeout(() => {
			setShow(false);
			closeTimeoutRef.current = null;
		}, 175);
	};

	useEffect(() => () => {
		if (closeTimeoutRef.current) {
			clearTimeout(closeTimeoutRef.current);
		}
	}, []);

	if (isListEmpty(messageEncounters)) {
		return (
			<OverlayTrigger
				placement="bottom"
				overlay={<Tooltip id="v2-messages-empty-tooltip">You have no messages right now.</Tooltip>}
			>
				<button
					type="button"
					className={`${styles.dashboardPageHeaderIconButton} ${styles.dashboardPageHeaderIconButtonOrange}`}
					aria-label="Messages"
				>
					<ChatIcon aria-hidden="true" />
				</button>
			</OverlayTrigger>
		);
	}

	return (
		<Dropdown
			align="end"
			show={show}
			onToggle={(next) => setShow(next)}
			onMouseEnter={openMenu}
			onMouseLeave={closeMenuWithDelay}
		>
			<Dropdown.Toggle as={ChatToggle} id="v2-messages-menu-toggle" hasUnread={isBooleanTrue(hasUnread)} />
			<Dropdown.Menu
				className={`${styles.headerMenu} dropdown-menu-end mt-2`}
				align="end"
				onMouseEnter={openMenu}
				onMouseLeave={closeMenuWithDelay}
			>
				<div className={styles.headerMenuHeader}>
					<span className={styles.headerMenuTitle}>Messages</span>
					<Link to={MESSAGES_ROUTE} className={styles.headerMenuViewAll}>View all</Link>
				</div>
				<div className={styles.headerMenuList}>
					{messageEncounters.map((encounter) => {
						const unreadCount = encounter.unreadCount || 0;
						const isUnread = !isBooleanTrue(encounter.isRead) && unreadCount > 0;
						return (
							<Link key={encounter.id} to={buildLink(encounter)} className={styles.headerMenuItem}>
								<EncounterIcon encounter={encounter} unreadCount={isBooleanTrue(isUnread) ? unreadCount : 0} />
								<div className={styles.headerMenuItemContent}>
									<div className={styles.headerMenuItemTopRow}>
										<span className={styles.headerMenuItemName}>{getDisplayName(encounter)}</span>
										<span className={styles.headerMenuItemDate}>{getLastMessageDateTime(encounter)}</span>
									</div>
									<p className={styles.headerMenuItemPreview}>{getPreview(encounter)}</p>
								</div>
							</Link>
						);
					})}
				</div>
			</Dropdown.Menu>
		</Dropdown>
	);
};
